<template>
  <div class="payment-code-page">
    <div class="payment-code-shell">
      <el-steps :active="activeStep" align-center finish-status="success">
        <el-step title="信息"></el-step>
        <el-step title="审核"></el-step>
        <el-step title="二维码"></el-step>
      </el-steps>

      <section v-if="activeStep === 0" class="step-panel">
        <h2>您想收多少?</h2>
        <p class="subtitle">输入收款金额并可选填付款方描述。</p>

        <el-form ref="form" :model="form" :rules="rules" label-position="top">
          <el-form-item label="金额" prop="amount">
            <el-input v-model="form.amount" class="amount-input" placeholder="0.00" @input="onAmountInput">
              <template slot="prepend">R$</template>
            </el-input>
            <div class="hint">最低 R$0.01，最高 R$10,000.00</div>
          </el-form-item>

          <el-form-item label="描述(可选)" prop="description">
            <el-input
              v-model="form.description"
              maxlength="144"
              show-word-limit
              placeholder="例如: user charge"
            ></el-input>
          </el-form-item>

          <el-button type="primary" class="wide-button" @click="goReview">继续</el-button>
        </el-form>
      </section>

      <section v-if="activeStep === 1" class="step-panel">
        <h2>确认收款信息</h2>
        <p class="subtitle">生成二维码前请核对以下信息。生成后有效期为 24 小时。</p>

        <div class="review-box">
          <div class="review-main">R${{ amountText }}</div>
          <div class="review-row">
            <span>描述</span>
            <strong>{{ form.description || '--' }}</strong>
          </div>
          <div class="review-row">
            <span>有效期</span>
            <strong>二维码生成后 24 小时</strong>
          </div>
        </div>

        <div class="button-row">
          <el-button @click="activeStep = 0">返回编辑</el-button>
          <el-button type="primary" :loading="loading" @click="createPaymentCode">生成二维码</el-button>
        </div>
      </section>

      <section v-if="activeStep === 2" class="step-panel result-panel">
        <h2>收款已就绪</h2>
        <p class="subtitle">在付款完成前保持页面打开，或稍后在代收订单中查看。</p>

        <div class="result-amount">R${{ resultAmountText }}</div>
        <div class="result-desc">{{ result.paymentDescription || form.description || '--' }}</div>

        <qrcode v-if="qrCodeValue" :value="qrCodeValue" :options="{ width: 280, margin: 2 }"></qrcode>

        <el-tag type="success" class="status-tag">等待付款</el-tag>

        <el-input v-if="qrCodeValue" :value="qrCodeValue" readonly class="copy-input">
          <el-button slot="append" icon="el-icon-document-copy" @click="copyPix">复制</el-button>
        </el-input>

        <div class="detail-box">
          <div class="review-row">
            <span>交易 ID</span>
            <strong>{{ result.orderId || '--' }}</strong>
          </div>
          <div class="review-row">
            <span>创建时间</span>
            <strong>{{ formatTime(result.createTimeL) }}</strong>
          </div>
          <div class="review-row">
            <span>过期时间</span>
            <strong>{{ formatTime(result.expireTimeL) }}</strong>
          </div>
        </div>

        <el-button type="primary" class="wide-button" @click="reset">接收新 PIX</el-button>
      </section>
    </div>
  </div>
</template>

<script>
import VueQrcode from '@chenfengyuan/vue-qrcode'
import moment from 'moment'

export default {
  name: 'PaymentCodeCreate',
  components: {
    qrcode: VueQrcode,
  },
  data() {
    const validateAmount = (rule, value, callback) => {
      if (!value) {
        callback(new Error('请输入金额'))
        return
      }
      if (!/^\d+(\.\d{1,2})?$/.test(value)) {
        callback(new Error('金额最多支持两位小数'))
        return
      }
      const number = Number(value)
      if (number < 0.01 || number > 10000) {
        callback(new Error('金额范围为 R$0.01 到 R$10,000.00'))
        return
      }
      callback()
    }
    return {
      activeStep: 0,
      loading: false,
      submittedAmount: '',
      form: {
        amount: '',
        description: '',
      },
      result: {},
      rules: {
        amount: [{ validator: validateAmount, trigger: 'blur' }],
        description: [{ max: 144, message: '描述最多 144 个字符', trigger: 'blur' }],
      },
    }
  },
  computed: {
    amountText() {
      return this.normalizeAmount(this.form.amount)
    },
    resultAmountText() {
      return this.normalizeAmount(this.submittedAmount || this.form.amount)
    },
    qrCodeValue() {
      return this.result.qrcodeRaw || this.result.payUrl || ''
    },
  },
  methods: {
    normalizeAmount(value) {
      const number = Number(value || 0)
      return Number.isFinite(number) ? number.toFixed(2) : '0.00'
    },
    onAmountInput(value) {
      let nextValue = value.replace(/[^\d.]/g, '')
      const firstDotIndex = nextValue.indexOf('.')
      if (firstDotIndex >= 0) {
        nextValue =
          nextValue.slice(0, firstDotIndex + 1) +
          nextValue.slice(firstDotIndex + 1).replace(/\./g, '')
      }
      this.form.amount = nextValue.replace(/^(\d+\.\d{0,2}).*$/, '$1')
    },
    goReview() {
      this.$refs.form.validate(valid => {
        if (valid) {
          this.activeStep = 1
        }
      })
    },
    createPaymentCode() {
      this.loading = true
      this.submittedAmount = this.amountText
      this.axios
        .post('/order/paymentCode/create', {
          amount: this.form.amount,
          description: this.form.description,
        })
        .then(res => {
          if (res.code === 200) {
            this.result = res.data || {}
            this.activeStep = 2
          }
        })
        .finally(() => {
          this.loading = false
        })
    },
    copyPix() {
      const text = this.qrCodeValue
      if (!text) {
        return
      }
      const textarea = document.createElement('textarea')
      textarea.value = text
      textarea.setAttribute('readonly', 'readonly')
      textarea.style.position = 'fixed'
      textarea.style.left = '-9999px'
      document.body.appendChild(textarea)
      textarea.select()
      try {
        if (document.execCommand('copy')) {
          this.$message.success('复制成功')
          return
        }
        this.$message.error('复制失败，请手动复制')
      } catch (e) {
        this.$message.error('复制失败，请手动复制')
      } finally {
        document.body.removeChild(textarea)
      }
    },
    formatTime(value) {
      const date = this.parseTime(value)
      if (!date) {
        return '--'
      }
      return date.format('YYYY/MM/DD HH:mm')
    },
    parseTime(value) {
      if (value === null || value === undefined || value === 0) {
        return null
      }

      if (value instanceof Date) {
        return this.validMoment(moment(value))
      }

      const text = String(value).trim()
      if (!text || text === '0' || text.toLowerCase() === 'null' || text.toLowerCase() === 'undefined') {
        return null
      }

      if (/^\d+$/.test(text)) {
        if (text.length === 14) {
          return this.validMoment(moment(text, 'YYYYMMDDHHmmss', true))
        }
        if (text.length === 13) {
          return this.validMoment(moment(Number(text)))
        }
        if (text.length === 10) {
          return this.validMoment(moment(Number(text) * 1000))
        }
        if (text.length === 8) {
          return this.validMoment(moment(text, 'YYYYMMDD', true))
        }
        return this.validMoment(moment(Number(text)))
      }

      const date = moment(
        text,
        [
          moment.ISO_8601,
          'YYYY-MM-DD HH:mm:ss',
          'YYYY/MM/DD HH:mm:ss',
          'YYYY-MM-DD HH:mm',
          'YYYY/MM/DD HH:mm',
        ],
        true
      )
      if (date.isValid()) {
        return date
      }

      const parsed = Date.parse(text)
      return Number.isNaN(parsed) ? null : this.validMoment(moment(parsed))
    },
    validMoment(date) {
      return date && date.isValid() ? date : null
    },
    reset() {
      this.activeStep = 0
      this.submittedAmount = ''
      this.form = {
        amount: '',
        description: '',
      }
      this.result = {}
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.clearValidate()
        }
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.payment-code-page {
  min-height: calc(100vh - 110px);
  background: #fff;
  color: #303133;
  padding: 28px;
}

.payment-code-shell {
  max-width: 920px;
  margin: 0 auto;
}

.step-panel {
  margin-top: 28px;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 34px;
  background: #fff;
}

h2 {
  margin: 0;
  text-align: center;
  font-size: 28px;
  font-weight: 700;
}

.subtitle {
  margin: 12px 0 28px;
  text-align: center;
  color: #606266;
}

.amount-input {
  font-size: 24px;
}

.hint {
  margin-top: 8px;
  color: #909399;
}

.wide-button {
  width: 100%;
  margin-top: 18px;
}

.review-box,
.detail-box {
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 24px;
}

.review-main,
.result-amount {
  text-align: center;
  font-size: 54px;
  font-weight: 700;
  line-height: 1.15;
}

.review-row {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 0;
  border-bottom: 1px solid #ebeef5;
}

.review-row span {
  color: #909399;
}

.review-row strong {
  min-width: 0;
  text-align: right;
  overflow-wrap: anywhere;
}

.review-row:last-child {
  border-bottom: 0;
}

.button-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;
  margin-top: 24px;
}

.result-panel {
  text-align: center;
}

.result-desc {
  margin-bottom: 22px;
  color: #909399;
  text-transform: uppercase;
  overflow-wrap: anywhere;
}

.status-tag {
  display: inline-flex;
  margin: 18px 0;
}

.copy-input {
  margin: 18px 0 26px;
}

@media (max-width: 720px) {
  .payment-code-page {
    padding: 16px;
  }

  .step-panel {
    padding: 22px 16px;
  }

  .review-main,
  .result-amount {
    font-size: 38px;
  }

  .button-row {
    grid-template-columns: 1fr;
  }

  .review-row {
    flex-direction: column;
    gap: 6px;
  }

  .review-row strong {
    text-align: left;
  }
}
</style>
